import { ref, watch, computed } from '@vue/composition-api';
import { title } from '@core/utils/filter';

// Notification
import { useToast } from 'vue-toastification/composition';
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue';

import moment from 'moment';
import store from '@/store';

export default function useSubjectCoursesList(subjectData) {
  // Use toast
  const toast = useToast();

  const refCourseListTable = ref(null);

  // Table Handlers
  const tableColumns = [
    { key: 'name', sortable: true },
    {
      key: 'startDate',
      label: 'Starts',
      formatter: (value) => {
        if (!value) return 'n/a';
        return moment(value).utc().format('L');
      },
      sortable: true,
    },
    {
      key: 'endDate',
      label: 'Ends',
      formatter: (value) => {
        if (!value) return 'n/a';
        return moment(value).utc().format('L');
      },
      sortable: true,
    },
    {
      key: 'profiles',
      label: 'Students',
    },
    {
      key: 'createdAt',
      label: 'Created',
      formatter: (value) => {
        return moment(value).format('L');
      },
      sortable: true,
    },
    { key: 'visibility', sortable: true },
    { key: 'status', sortable: true },
    { key: 'actions' },
  ];
  const perPage = ref(10);
  const totalCourses = ref(0);
  const currentPage = ref(1);
  const perPageOptions = [10, 25, 50, 100];
  const searchQuery = ref('');
  const sortBy = ref('createdAt');
  const isSortDirDesc = ref(true);

  const visibilityFilter = ref(null);
  const statusFilter = ref(null);

  const dataMeta = computed(() => {
    const localItemsCount = refCourseListTable.value ? refCourseListTable.value.localItems.length : 0;
    return {
      from: perPage.value * (currentPage.value - 1) + (localItemsCount ? 1 : 0),
      to: perPage.value * (currentPage.value - 1) + localItemsCount,
      of: totalCourses.value,
    };
  });

  const refetchData = () => {
    if (refCourseListTable.value) {
      refCourseListTable.value.refresh();
    }
  };

  watch([currentPage, perPage, searchQuery, visibilityFilter, statusFilter, subjectData], () => {
    refetchData();
  });

  const fetchCourses = (ctx, callback) => {
    const pw = subjectData.value;

    const sbv = sortBy.value === 'id' ? '_id' : sortBy.value;
    const sb = `${sbv}:${isSortDirDesc.value ? 'desc' : 'asc'}`;

    const pl = {
      search: searchQuery.value,
      limit: perPage.value,
      page: currentPage.value,
      sortBy: sb,
      subject: pw.id,

      populate: 'classes',
    };

    if (visibilityFilter.value) {
      pl.visibility = visibilityFilter.value;
    }
    if (statusFilter.value) {
      pl.status = statusFilter.value;
    }

    console.log(`course payload`, pl);

    store
      .dispatch('app-roster/fetchCourses', pl)
      .then((response) => {
        const { results, totalResults } = response.data;

        callback(results);
        totalCourses.value = totalResults;
      })
      .catch(() => {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Error fetching courses list',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        });
      });
  };

  // *===============================================---*
  // *--------- UI ---------------------------------------*
  // *===============================================---*

  const resolveUserRoleVariant = (role) => {
    if (role === 'subscriber') return 'primary';
    if (role === 'author') return 'warning';
    if (role === 'maintainer') return 'success';
    if (role === 'editor') return 'info';
    if (role === 'admin') return 'danger';
    return 'primary';
  };

  const resolveUserRoleIcon = (role) => {
    if (role === 'subscriber') return 'CourseIcon';
    if (role === 'author') return 'SettingsIcon';
    if (role === 'maintainer') return 'DatabaseIcon';
    if (role === 'editor') return 'Edit2Icon';
    if (role === 'admin') return 'ServerIcon';
    return 'CourseIcon';
  };

  const resolveCourseVisibilityVariant = (status) => {
    if (status === 'internal') return 'primary';
    if (status === 'public') return 'info';
    return 'primary';
  };

  const resolveCourseStatusVariant = (status) => {
    if (status === 'created') return 'secondary';
    if (status === 'enrolling') return 'primary';
    if (status === 'waiting') return 'warning';
    if (status === 'live') return 'info';
    if (status === 'completed') return 'success';
    return 'primary';
  };

  const statusOptions = [
    { label: 'Created', value: 'created' },
    { label: 'Enrolling', value: 'enrolling' },
    { label: 'Waiting', value: 'waiting' },
    { label: 'Live', value: 'live' },
    { label: 'Completed', value: 'completed' },
  ];

  return {
    fetchCourses,
    tableColumns,
    perPage,
    currentPage,
    totalCourses,
    dataMeta,
    perPageOptions,
    searchQuery,
    sortBy,
    isSortDirDesc,
    refCourseListTable,

    resolveUserRoleVariant,
    resolveUserRoleIcon,
    resolveCourseVisibilityVariant,
    resolveCourseStatusVariant,
    refetchData,

    statusOptions,

    // Extra Filters
    visibilityFilter,
    statusFilter,
  };
}
